import React, { forwardRef } from 'react';

import analytics from '@repo/analytics';
import Tag from '@repo/editor/src/components/Tag/Tag';
import { themeStyle } from '@repo/ui/theming';
import IconLucideBell from '@repo/ui-icons/IconLucideBell';
import { H2, H4 } from '@repo/ui-kit/components/core/Typography';

import { LANDING_PAGES_NAVIGATION_CLICK } from 'global/analyticsEvents';
import { NavigationComponentWrapper } from 'strapi/component/categoryList/styles';
import { CategoryListNavigationProps } from 'strapi/component/categoryList/types';
import { getSectionIcon } from 'strapi/component/iconUtils';
import { FlexColumn, FlexRow } from 'styles/layouts';

import { ComponentContainer } from '../shared/styles';

const tagTheme = {
  backgroundColor: 'transparent',
  backgroundColorHover: themeStyle.varInkSuperLight,
  color: 'var(--color-text)',
  borderColor: 'var(--color-text)',
  fontWeight: themeStyle.fontSemiBold,
  borderRadius: '100px',
  letterSpacing: 'normal',
  height: '40px',
  padding: '8px 16px 8px 12px',
};

export const CategoryListNavigation = forwardRef<
  Record<string, HTMLDivElement>,
  CategoryListNavigationProps
>(({ categories }, categoriesRefs) => {
  const handleClick = (name: string) => {
    if (typeof categoriesRefs !== 'function') {
      categoriesRefs?.current?.[name]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      analytics.track(LANDING_PAGES_NAVIGATION_CLICK, {
        component: 'categoryList',
        title: name,
      });
    }
  };

  const title = 'Quick navigation';

  return (
    <NavigationComponentWrapper>
      <ComponentContainer>
        <FlexColumn gap="24px">
          <H2 bold>{title}</H2>
          <FlexRow flexWrap="wrap" gap="24px">
            {categories.map((category) => {
              const Icon = category.icon
                ? getSectionIcon(category.icon, IconLucideBell)
                : undefined;
              return (
                <Tag
                  key={category.name}
                  theme={tagTheme}
                  onClick={() => handleClick(category.name)}
                >
                  <FlexRow alignItems="center" gap="8px">
                    {!!Icon && <Icon color="var(--color-text)" />}
                    <H4 as="p">{category.text}</H4>
                  </FlexRow>
                </Tag>
              );
            })}
          </FlexRow>
        </FlexColumn>
      </ComponentContainer>
    </NavigationComponentWrapper>
  );
});

CategoryListNavigation.displayName = 'CategoryListNavigation';
